import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { MagnifyingGlass, ArrowSquareOut } from '@phosphor-icons/react';
import { InviteModal } from './InviteModal';
import { SAUsersSkeleton } from './SASkeletons';

const monoFont = "'IBM Plex Mono', monospace";

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  site_name: string;
  plan: string;
  status: string;
  last_access_at: string | null;
  created_at: string;
  billing_email: string | null;
  is_internal: boolean;
}

interface CmsUsage {
  user_id: string;
  disk_usage_bytes: number;
}

type FilterStatus = 'all' | 'active' | 'inactive' | 'pending' | 'suspended' | 'cancelled';

const statusColors: Record<string, string> = {
  active: '#00E87A', pending: 'rgba(247,246,242,0.4)', suspended: '#F0E614', cancelled: '#FF3F2D',
};

const statusLabels: Record<string, string> = {
  active: 'ATIVO', pending: 'PENDENTE', suspended: 'SUSPENSO', cancelled: 'CANCELADO',
};

function relativeTime(d: string | null) {
  if (!d) return 'nunca';
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'hoje';
  if (days === 1) return 'ontem';
  return `${days}d atrás`;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
}

export function SAUsers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [usageMap, setUsageMap] = useState<Record<string, number>>({});
  const [viewsMap, setViewsMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>((searchParams.get('filtro') as FilterStatus) || 'all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'lastAccess'>('recent');
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('user_profiles').select('*').order('created_at', { ascending: false });
      if (data) setUsers(data as any[]);

      const { data: usage } = await supabase.from('cms_usage').select('user_id, disk_usage_bytes');
      if (usage) {
        const map: Record<string, number> = {};
        (usage as any[]).forEach(u => { map[u.user_id] = u.disk_usage_bytes; });
        setUsageMap(map);
      }

      // Views last 30d per user (dados reais via edge function get-user-analytics)
      const profiles = (data as any[]) || [];
      if (profiles.length > 0) {
        const results = await Promise.allSettled(
          profiles.map(async (u) => {
            const { data: analytics, error } = await supabase.functions.invoke('get-user-analytics', {
              body: { username: u.username },
            });
            if (error) throw error;
            return { user_id: u.user_id, views: (analytics as any)?.pageviews ?? 0 };
          })
        );
        const vmap: Record<string, number> = {};
        profiles.forEach((u, i) => {
          const r = results[i];
          vmap[u.user_id] = r.status === 'fulfilled' ? (r.value.views ?? 0) : 0;
        });
        setViewsMap(vmap);
      }

      setLoading(false);
    };
    load();
  }, []);

  const filtered = users.filter(u => {
    if (filter === 'inactive') {
      if (u.status !== 'active') return false;
      if (u.last_access_at && (Date.now() - new Date(u.last_access_at).getTime()) < 30 * 86400000) return false;
      return true;
    }
    if (filter !== 'all' && u.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return u.username.toLowerCase().includes(s) || u.site_name.toLowerCase().includes(s) || (u.billing_email || '').toLowerCase().includes(s);
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'recent') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    if (!a.last_access_at && !b.last_access_at) return 0;
    if (!a.last_access_at) return -1;
    if (!b.last_access_at) return 1;
    return new Date(b.last_access_at).getTime() - new Date(a.last_access_at).getTime();
  });

  const chipStyle = (active: boolean): React.CSSProperties => ({
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
    padding: '5px 10px', whiteSpace: 'nowrap',
    border: 'none', backgroundColor: active ? 'rgba(247,246,242,0.1)' : 'transparent',
    color: active ? '#F7F6F2' : 'rgba(247,246,242,0.35)', cursor: 'pointer',
    transition: 'all 0.15s ease', letterSpacing: '0.03em',
  });

  const activeCount = users.filter(u => u.status === 'active').length;
  const pendingCount = users.filter(u => u.status === 'pending').length;
  const totalStorage = Object.values(usageMap).reduce((sum, v) => sum + (v || 0), 0);

  if (loading) return <SAUsersSkeleton />;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontFamily: monoFont, fontSize: 18, fontWeight: 300, color: '#F7F6F2' }}>Usuários</div>
        <button
          onClick={() => setShowInvite(true)}
          style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 14px', border: '0.5px solid #00E87A',
            backgroundColor: 'rgba(0,232,122,0.08)', color: '#00E87A', cursor: 'pointer',
          }}
        >+ Convidar</button>
      </div>

      <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)', marginBottom: 12 }}>
        {activeCount} ativos · {pendingCount} pendente{pendingCount !== 1 ? 's' : ''} · {formatBytes(totalStorage)}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <MagnifyingGlass size={14} weight="thin" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(247,246,242,0.3)' }} />
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          aria-label="Buscar usuário por username ou email"
          placeholder="Buscar username ou email..."
          style={{
            fontFamily: monoFont, fontSize: 13, fontWeight: 300, width: '100%',
            padding: '10px 12px 10px 34px', border: '0.5px solid rgba(247,246,242,0.12)',
            backgroundColor: 'rgba(247,246,242,0.04)', color: 'rgba(247,246,242,0.85)',
          }}
        />
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {(['all', 'active', 'inactive', 'pending', 'suspended', 'cancelled'] as FilterStatus[]).map(f => (
          <button key={f} onClick={() => setFilter(f)} style={chipStyle(filter === f)}>
            {f === 'all' ? 'Todos' : f === 'inactive' ? 'Inativos' : statusLabels[f] || f}
          </button>
        ))}
      </div>

      {/* Sort selector */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
        <button onClick={() => setSortBy('recent')} style={chipStyle(sortBy === 'recent')}>Mais recente</button>
        <button onClick={() => setSortBy('lastAccess')} style={chipStyle(sortBy === 'lastAccess')}>Último acesso</button>
      </div>

      {/* User cards */}
      {sorted.length === 0 ? (
        <div style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.3)', textAlign: 'center', padding: '40px 0' }}>
          Nenhum usuário encontrado
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sorted.map(u => (
            <button
              key={u.id}
              onClick={() => navigate(`/admin/usuarios/${u.user_id}`)}
              style={{
                backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)',
                padding: 14, textAlign: 'left', cursor: 'pointer', width: '100%',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(247,246,242,0.15)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(247,246,242,0.12)'}
            >
              {/* Top row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 36, height: 36, backgroundColor: 'rgba(247,246,242,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: monoFont, fontSize: 14, fontWeight: 300, color: 'rgba(247,246,242,0.5)',
                  flexShrink: 0,
                }}>
                  {u.username[0]?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: '#F7F6F2' }}>{u.username}</div>
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.billing_email || u.site_name}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                  <span style={{
                    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                    padding: '2px 6px', border: `0.5px solid ${statusColors[u.status] || 'rgba(247,246,242,0.2)'}`,
                    color: statusColors[u.status] || 'rgba(247,246,242,0.4)',
                  }}>
                    {statusLabels[u.status] || u.status.toUpperCase()}
                  </span>
                  <span style={{
                    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                    padding: '2px 6px', border: '0.5px solid rgba(247,246,242,0.15)',
                    color: u.plan === 'pro' ? '#F7F6F2' : 'rgba(247,246,242,0.4)',
                  }}>
                    {u.plan.toUpperCase()}
                  </span>
                  {u.is_internal && (
                    <span style={{
                      fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                      padding: '2px 6px', border: '0.5px solid rgba(247,246,242,0.15)',
                      color: 'rgba(247,246,242,0.3)',
                    }}>
                      INTERNO
                    </span>
                  )}
                  <a
                    href={`https://${u.username}.criaaa.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ display: 'flex', alignItems: 'center', color: 'rgba(247,246,242,0.4)', transition: 'color 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'rgba(247,246,242,0.8)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'rgba(247,246,242,0.4)'; }}
                  >
                    <ArrowSquareOut size={14} weight="thin" />
                  </a>
                </div>
              </div>
              {/* Bottom row */}
              <div style={{ borderTop: '0.5px solid rgba(247,246,242,0.12)', paddingTop: 8, display: 'flex', gap: 16 }}>
                <div>
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', color: 'rgba(247,246,242,0.3)', marginBottom: 2 }}>Acesso CMS</div>
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.7)' }}>{relativeTime(u.last_access_at)}</div>
                </div>
                <div>
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', color: 'rgba(247,246,242,0.3)', marginBottom: 2 }}>Storage</div>
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.7)' }}>{formatBytes(usageMap[u.user_id] || 0)}</div>
                </div>
                <div>
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', color: 'rgba(247,246,242,0.3)', marginBottom: 2 }}>Views/30d</div>
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.7)' }}>{viewsMap[u.user_id] || 0}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onInviteSent={() => setShowInvite(false)} />}
    </div>
  );
}
